import { Typography } from '@mui/material';
import React from 'react';

import { CollapseSection } from 'shared/components/layout/Collapse/CollapseSection';

import css from './EntityMetadataImportGuide.module.scss';

export function EntityMetadataImportGuide() {
  return (
    <>
      <Typography mb={1.5} variant='body2'>
        Import Entity metadata by uploading a CSV file with one of the following
        formats:
      </Typography>
      <ol>
        <li>
          <Typography variant='code'>
            id, suppressed, criticality, tags, tags_to_add, tags_to_remove
          </Typography>
        </li>
        <li>
          <Typography variant='code'>
            type, value, suppressed, criticality, tags, tags_to_add,
            tags_to_remove
          </Typography>
        </li>
      </ol>
      <Typography mb={1.5} variant='body2'>
        Note: There must be at least one column of data uploaded excluding the
        required columns for the specified format.
      </Typography>
      <CollapseSection
        prompt='Show Import Guide'
        promptToHide='Hide Import Guide'
      >
        <div className={css.guide}>
          <Typography variant='h3' mb={1}>
            Import Entity Metadata
          </Typography>
          <Typography variant='body2' mb={1}>
            When you create a .csv file, use the following fields. Each
            &quot;optional&quot; below corresponds to the value, not the header.
            In other words, the headers are always required in the CSV file.
          </Typography>
          <ul className={css.fieldList}>
            <li>
              <Typography variant='body2'>
                id (required for first format)
              </Typography>
              <Typography variant='body2' mb={1}>
                The type and value of Entity (e.g.{' '}
                <Typography variant='code'>_ip-1.2.3.4</Typography>).
              </Typography>
            </li>
            <li>
              <Typography variant='body2'>
                type (required for second format)
              </Typography>
              <Typography variant='body2' mb={1}>
                The type of Entity (e.g.{' '}
                <Typography variant='code'>_ip</Typography>).
              </Typography>
            </li>
            <li>
              <Typography variant='body2'>
                value (required for second format)
              </Typography>
              <Typography variant='body2' mb={1}>
                The value of the Entity (e.g.{' '}
                <Typography variant='code'>1.2.3.4</Typography>).
              </Typography>
            </li>
            <li>
              <Typography variant='body2'>suppressed (optional)</Typography>
              <Typography variant='body2' mb={1}>
                When true, Entities with the corresponding value are suppressed.
              </Typography>
            </li>
            <li>
              <Typography variant='body2'>criticality (optional)</Typography>
              <Typography variant='body2' mb={1}>
                Sets the severity of Signals for this Entity based on some risk
                factor or other consideration. This must be a criticality
                already defined in the application.
              </Typography>
            </li>
            <li>
              <Typography variant='body2'>tags (optional)</Typography>
              <Typography variant='body2' mb={1}>
                Sets the specified metadata for the Entity to add context. This
                value cannot be present if tags_to_add or tags_to_remove are
                defined.
              </Typography>
            </li>
            <li>
              <Typography variant='body2'>tags_to_add (optional)</Typography>
              <Typography variant='body2' mb={1}>
                Used to add tags to existing Entities. This value cannot be
                present if tags is defined.
              </Typography>
            </li>
            <li>
              <Typography variant='body2'>tags_to_remove (optional)</Typography>
              <Typography variant='body2' mb={1}>
                Used to remove tags from existing Entities. This value cannot be
                present if tags is defined.
              </Typography>
            </li>
          </ul>

          <Typography variant='body2' mb={1}>
            Here is an example of a file using the first format:
          </Typography>
          <Typography variant='code'>
            id,suppressed,criticality,tags,tags_to_add,tags_to_remove
            <br />
            _ip-10.0.0.5,false,null,,Office-Based,
          </Typography>

          <Typography variant='body2' my={1}>
            Here is an example of a file using the second format:
          </Typography>
          <Typography variant='code'>
            type,value,suppressed,criticality,tags
            <br />
            _ip,10.0.0.5,false,null,Executive PC
          </Typography>
        </div>
      </CollapseSection>
    </>
  );
}
